/**
 * SixDigitCode represents a validated 6-digit verification code (000000-999999)
 */
export class SixDigitCode {
  private readonly value: string;

  private constructor(value: string) {
    this.value = value;
  }

  /**
   * Creates a new SixDigitCode from a string input.
   * Validates that the input contains exactly 6 numeric digits.
   * @param input - String input to validate (whitespace is stripped)
   * @returns SixDigitCode instance
   * @throws Error if input is invalid
   */
  static create(input: string): SixDigitCode {
    if (!input || input.trim() === '') {
      throw new Error('Code cannot be empty');
    }

    // Remove all whitespace
    const cleanCode = input.replace(/\s/g, '');

    // Check if it's exactly 6 characters
    if (cleanCode.length !== 6) {
      throw new Error(
        `Code must be exactly 6 digits (got ${cleanCode.length} characters)`
      );
    }

    // Validate that all characters are digits
    if (!/^\d{6}$/.test(cleanCode)) {
      throw new Error('Code must be exactly 6 numeric digits');
    }

    return new SixDigitCode(cleanCode);
  }

  /**
   * Attempts to create a SixDigitCode from a string input.
   * Returns null if the input is invalid instead of throwing an error.
   * @param input - String input to validate
   * @returns SixDigitCode instance or null if invalid
   */
  static tryCreate(input: string): SixDigitCode | null {
    try {
      return SixDigitCode.create(input);
    } catch {
      return null;
    }
  }

  /**
   * Returns the code as a string
   */
  toString(): string {
    return this.value;
  }

  /**
   * Checks if two SixDigitCode instances are equal
   */
  equals(other: SixDigitCode): boolean {
    return this.value === other.value;
  }

  /**
   * JSON serialization support
   */
  toJSON(): string {
    return this.value;
  }

  /**
   * Creates a SixDigitCode from JSON
   */
  static fromJSON(json: string): SixDigitCode {
    return SixDigitCode.create(json);
  }
}
